// Mini Markdown → HTML (clean & safe):
// - **bold** / __bold__         → <strong>
// - [text](https://...)         → <a ... target="_blank" rel="noopener noreferrer">
// - [name](mailto:x@y.com)      → <a href="mailto:...">email</a> (visible text = exact email)
// - Plain email                 → <a href="mailto:...">email</a>
// - Plain URL                   → <a ... target="_blank" rel="noopener noreferrer">
// - Preserve existing <a> tags (mask & restore) with salted placeholders

const SENTINEL = '\x1EANCHOR\x1E';

const RE_EXISTING_ANCHOR = /<a\b[^>]*>[\s\S]*?<\/a>/gi;
const RE_MD_MAILTO = /\[([^\]]+?)\]\(\s*mailto:([^)>\s]+?)\s*\)/gi;
const RE_MD_LINK = /\[([^\]]+?)\]\(\s*(https?:\/\/[^\s)]+?)\s*\)/gi;
const RE_BOLD = /(\*\*|__)(.+?)\1/g;
const RE_EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const RE_PLAIN_URL = /\bhttps?:\/\/[^\s<>()]+/gi;

// --- salted masking to avoid collisions across multiple mask/restore passes ---
let MASK_SEQ = 0;
function uniqueSalt() {
  return `S${Date.now()}_${MASK_SEQ++}`;
}

// Safely extract offset and fullString from the replace callback arguments
function getOffsetAndFull(rest) {
  const last = rest[rest.length - 1];
  if (typeof last === 'object' && last !== null) {
    // ...captures, offset, fullString, groups
    return {offset: rest[rest.length - 3], full: rest[rest.length - 2]};
  }
  // ...captures, offset, fullString
  return {offset: rest[rest.length - 2], full: rest[rest.length - 1]};
}

// Replace existing <a> tags with unique placeholders to avoid re-linking them
function maskAnchors(input) {
  const anchors = [];
  const salt = uniqueSalt();
  const text = input.replace(RE_EXISTING_ANCHOR, m => {
    const key = `${SENTINEL}${salt}:${anchors.length}${SENTINEL}`;
    anchors.push({key, html: m});
    return key;
  });
  return {text, anchors};
}

// Restore masked <a> tags after processing
function restoreAnchors(text, anchors) {
  for (const {key, html} of anchors) {
    text = text.split(key).join(html);
  }
  return text;
}

// Check if a position is inside an HTML tag
function isInsideTag(full, index) {
  const lastLt = full.lastIndexOf('<', index);
  const lastGt = full.lastIndexOf('>', index);
  return lastLt > lastGt;
}

// Autolink plain email addresses
function autolinkPlainEmails(text) {
  return text.replace(RE_EMAIL, (match, ...rest) => {
    const {offset, full} = getOffsetAndFull(rest);

    // Skip if preceded by 'mailto:'
    const prev = full.slice(Math.max(0, offset - 8), offset).toLowerCase();
    if (prev.endsWith('mailto:')) return match;

    // Skip if inside an HTML tag
    if (isInsideTag(full, offset)) return match;

    return `<a href="mailto:${match}">${match}</a>`;
  });
}

// Autolink plain URLs; trims trailing punctuation like .,!?)]}
function autolinkPlainUrls(text) {
  return text.replace(RE_PLAIN_URL, (match, ...rest) => {
    const {offset, full} = getOffsetAndFull(rest);

    // Skip if inside an HTML tag
    if (isInsideTag(full, offset)) return match;

    // Trim trailing punctuation that doesn't belong to the URL
    const trailing = /[.,!?)\]}]+$/.exec(match);
    let core = match;
    let tail = '';
    if (trailing) {
      tail = trailing[0];
      core = match.slice(0, match.length - tail.length);
    }

    return `<a href="${core}" target="_blank" rel="noopener noreferrer">${core}</a>${tail}`;
  });
}

/**
 * Parse Markdown syntax into HTML
 * Supports: headings, bold, italic, links, lists, blockquotes, code blocks, inline code
 * @param {string} input
 * @returns {string}
 */
export function parseMarkdown(input = '') {
  if (!input) return '';

  // 1) Mask existing <a> tags to avoid double-wrap (salted)
  const {text: masked, anchors} = maskAnchors(input);

  let html = masked;

  // 2) Process line by line for block-level elements
  const lines = html.split('\n');
  const processed = [];
  let inCodeBlock = false;
  let codeBlockContent = [];
  let inList = false;
  let listItems = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code blocks (```)
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        processed.push(`<pre><code>${codeBlockContent.join('\n')}</code></pre>`);
        codeBlockContent = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      continue;
    }

    // Close previous list if needed
    if (inList && !line.match(/^[-*+]\s/) && !line.match(/^\d+\.\s/)) {
      processed.push(`<ul>${listItems.join('')}</ul>`);
      listItems = [];
      inList = false;
    }

    // Headings (# ## ### #### ##### ######)
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const content = headingMatch[2];
      processed.push(`<h${level}>${content}</h${level}>`);
      continue;
    }

    // Unordered lists (- * +)
    const ulMatch = line.match(/^[-*+]\s+(.+)$/);
    if (ulMatch) {
      inList = true;
      listItems.push(`<li>${ulMatch[1]}</li>`);
      continue;
    }

    // Ordered lists (1. 2. 3.)
    const olMatch = line.match(/^\d+\.\s+(.+)$/);
    if (olMatch) {
      if (!inList) {
        inList = 'ol';
      }
      listItems.push(`<li>${olMatch[1]}</li>`);
      continue;
    }

    // Blockquotes (>)
    const quoteMatch = line.match(/^>\s+(.+)$/);
    if (quoteMatch) {
      processed.push(`<blockquote>${quoteMatch[1]}</blockquote>`);
      continue;
    }

    // Regular line
    processed.push(line);
  }

  // Close any remaining list
  if (inList) {
    const tag = inList === 'ol' ? 'ol' : 'ul';
    processed.push(`<${tag}>${listItems.join('')}</${tag}>`);
  }

  html = processed.join('\n');

  // 3) Inline elements
  // [name](mailto:email) → always show the exact email as visible text
  html = html.replace(RE_MD_MAILTO, (m, _label, email) => {
    return `<a href="mailto:${email}">${email}</a>`;
  });

  // [text](https://...)
  html = html.replace(RE_MD_LINK, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // **bold** and __bold__
  html = html.replace(RE_BOLD, '<strong>$2</strong>');

  // *italic* and _italic_ (but not inside words)
  html = html.replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, '<em>$1</em>');
  html = html.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');

  // Inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Always autolink URLs
  html = autolinkPlainUrls(html);

  // Autolink emails ONLY if not skipped; use a separate salted mask to prevent collisions
  {
    const {text: emailMasked, anchors: emailAnchors} = maskAnchors(html);
    const afterEmail = autolinkPlainEmails(emailMasked);
    html = restoreAnchors(afterEmail, emailAnchors);
  }

  // Restore masked <a> tags from original input
  return restoreAnchors(html, anchors);
}

/**
 * @param {string} input
 * @returns {string}
 */
export function replaceEmailsWithHref(input = '') {
  if (!input) return '';
  const {text: masked, anchors} = maskAnchors(input);

  // [name](mailto:email) → always show the exact email
  let html = masked.replace(RE_MD_MAILTO, (m, _label, email) => {
    return `<a href="mailto:${email}">${email}</a>`;
  });

  // Autolink plain emails with another salted mask layer
  {
    const {text: emailMasked, anchors: emailAnchors} = maskAnchors(html);
    const afterEmail = autolinkPlainEmails(emailMasked);
    html = restoreAnchors(afterEmail, emailAnchors);
  }

  return restoreAnchors(html, anchors);
}

/**
 * @param {string} input
 * @returns {string}
 */
export function replaceLinkInText(input = '') {
  if (!input || typeof input !== 'string') return input;
  const {text: masked, anchors} = maskAnchors(input);

  // [name](mailto:email) → always show the exact email
  let html = masked.replace(RE_MD_MAILTO, (m, _label, email) => {
    return `<a href="mailto:${email}">${email}</a>`;
  });

  html = autolinkPlainUrls(html);

  // Autolink plain emails with another salted mask layer
  {
    const {text: emailMasked, anchors: emailAnchors} = maskAnchors(html);
    const afterEmail = autolinkPlainEmails(emailMasked);
    html = restoreAnchors(afterEmail, emailAnchors);
  }

  return restoreAnchors(html, anchors);
}

/**
 * Check if text contains Markdown syntax that parseMarkdown can handle
 * Supports: headings, bold, italic, links, lists, blockquotes, code blocks, inline code
 * @param {string} text
 * @returns {boolean}
 */
export function hasMarkdownSyntax(text = '') {
  if (!text || typeof text !== 'string') return false;

  // Check for patterns that parseMarkdown supports
  const markdownPatterns = [
    /^#{1,6}\s/m,                 // # heading
    /\*\*[^*]+\*\*/,              // **bold**
    /__[^_]+__/,                  // __bold__
    /(?<!\w)\*[^*]+\*(?!\w)/,     // *italic*
    /(?<!\w)_[^_]+_(?!\w)/,       // _italic_
    /\[[^\]]+\]\([^)]+\)/,        // [link](url)
    /^[-*+]\s/m,                  // - list
    /^\d+\.\s/m,                  // 1. ordered list
    /^>\s/m,                      // > blockquote
    /```[\s\S]*?```/,             // ```code block```
    /`[^`]+`/                     // `inline code`
  ];

  return markdownPatterns.some(pattern => pattern.test(text));
}
